import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NgClass, NgIf } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * Sensor types as the firmware auto-detects them (rc_zb_dev_type_t). Translation keys,
 * not text: the template pipes them, so a language switch repaints them for free.
 */
const TYPES: Record<number, string> = {
  0: 'zigbee.type.unknown',
  1: 'zigbee.type.leak',
  2: 'zigbee.type.climate',
  3: 'zigbee.type.motion',
};

@Component({
  selector: 'app-zigbee-card',
  templateUrl: './zigbee-card.component.html',
  styleUrls: ['./zigbee-card.component.scss'],
  standalone: true,
  imports: [TranslatePipe, NgIf, NgClass],
})
export class ZigbeeCardComponent {
  @Input() device: any;
  @Input() mac: any;
  @Input() ruleCount = 0;

  constructor(private router: Router) {}

  get typeName(): string {
    return TYPES[this.device?.type] ?? 'zigbee.type.unknown';
  }

  get isClimate(): boolean {
    return this.device?.type === 2;
  }

  /** Readings come as hundredths; a climate sensor has two of them, not one. */
  get tempText(): string {
    const v = this.device?.lastTemp;
    return v === undefined || v === null ? '' : `${(v / 100).toFixed(1)} °C`;
  }

  get humText(): string {
    const v = this.device?.lastHum;
    return v === undefined || v === null ? '' : `${(v / 100).toFixed(1)} %`;
  }

  /** For leak/motion sensors the state IS the event ("leak", "no leak"). */
  get stateText(): string {
    const e: string = this.device?.lastEvent ?? '';
    if (!e || e.endsWith('REPORT')) return '';
    return 'zigbee.event.' + e.toLowerCase();
  }

  get alarm(): boolean {
    const e: string = this.device?.lastEvent ?? '';
    return e === 'LEAK' || e === 'MOTION';
  }

  get battery(): number | null {
    const b = this.device?.battery;
    return b === undefined || b === null || b === 0 ? null : b;
  }

  /** Green / amber / red — a dead battery on a leak sensor is worth shouting about. */
  get batteryClass(): string {
    const b = this.battery;
    if (b === null) return '';
    if (b < 5) return 'critical';
    if (b < 30) return 'low';
    return 'ok';
  }

  onEdit(): void {
    this.router.navigate(['/zigbee-edit', this.device.ieee], { state: { controllerMac: this.mac } });
  }
}
